<script lang="ts">
export default {
  name: 'PublishDocumentListModal',
};
</script>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import { documentService } from '@/services/v2/DocumentService';
import { DocumentWithOtherData } from '@/types/v2/Document';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';

const props = withDefaults(defineProps<{
  accountId: number;
  docType: 'Binder' | 'Policy';
  title?: string;
}>(), {
  title: undefined,
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'documents-published'): void;
}>();

const modalTitle = computed(() => props.title ?? (props.docType === 'Binder' ? 'Publish Binders' : 'Publish Policy Documents'));

const unpublishedDocuments = ref<DocumentWithOtherData[]>([]);
const selectedDocumentIds = ref<Set<string>>(new Set());
const isSaving = ref(false);
const isLoading = ref(true);

function toggleDocument(documentId: string) {
  if (selectedDocumentIds.value.has(documentId)) {
    selectedDocumentIds.value.delete(documentId);
  } else {
    selectedDocumentIds.value.add(documentId);
  }
}

function isSelected(documentId: string): boolean {
  return selectedDocumentIds.value.has(documentId);
}

const selectedDocuments = computed(() =>
  unpublishedDocuments.value.filter((doc) => selectedDocumentIds.value.has(doc.documentId)),
);

async function handleSave() {
  if (selectedDocuments.value.length === 0) return;

  isSaving.value = true;
  try {
    await Promise.all(selectedDocuments.value.map((doc) => documentService.publishDocument(doc.documentId)));
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Documents published successfully!' });
    emit('documents-published');
    emit('close');
  } catch (e) {
    console.error(e);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to publish documents', color: 'error' });
  } finally {
    isSaving.value = false;
  }
}

onMounted(async () => {
  try {
    const allDocuments = await documentService.getDocumentsWithOtherDataByAccount(props.accountId);
    unpublishedDocuments.value = allDocuments.filter(
      (doc) => !doc.published && doc.documentTypeName === props.docType,
    );
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <AppModal
    :show="true"
    :title="modalTitle"
    size="xl"
  >
    <template #content>
      <div v-if="isLoading" class="flex justify-center py-4">
        <span class="loading loading-spinner loading-sm text-gray-400"></span>
      </div>
      <div v-else-if="unpublishedDocuments.length === 0" class="flex justify-center py-4 text-sm text-gray-500">
        No unpublished {{ docType === 'Binder' ? 'binders' : 'policy documents' }} found.
      </div>
      <div v-else class="max-h-[50vh] overflow-y-auto">
      <table class="apptable dense --tight-first">
        <thead>
          <tr>
            <td class="nosort"></td>
            <td class="nosort">Year</td>
            <td class="nosort">Policy</td>
            <td class="nosort">File Name</td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="doc in unpublishedDocuments"
            :key="doc.documentId"
            class="cursor-pointer"
            @click="toggleDocument(doc.documentId)"
          >
            <td class="nosort">
              <input
                type="checkbox"
                class="checkbox checkbox-sm checkbox-primary"
                :checked="isSelected(doc.documentId)"
                @click.stop="toggleDocument(doc.documentId)"
              />
            </td>
            <td class="nosort">{{ doc.policyEffectiveYear }}</td>
            <td class="nosort">
              <span class="font-semibold">{{ doc.mainPolicyNumber }}</span>
              <span v-if="doc.policyDescription" class="text-gray-500"> - {{ doc.policyDescription }}</span>
            </td>
            <td class="nosort">{{ doc.displayName ?? doc.s3Name }}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </template>

    <template #actions>
      <AppButton @click="$emit('close')">Cancel</AppButton>
      <AppButton primary :disabled="selectedDocuments.length === 0" :loading="isSaving" @click="handleSave">Save</AppButton>
    </template>
  </AppModal>
</template>
